import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, MapPin, Video } from 'lucide-react';
import { publicAPI } from '@/lib/publicApi';
import { themeForCategory } from '@/lib/eventCategoryTheme';
import { formatEventDate, formatEventTime } from '@/lib/eventTime';

export default function PublicEventsPage() {
  const [window, setWindow] = useState('upcoming');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    publicAPI.listPublicEvents({ window, page: 1, page_size: 24 })
      .then((r) => {
        setItems(r.data.items || []);
        setHasMore(!!r.data.has_more);
        setTotal(r.data.total || 0);
      })
      .catch(() => { setItems([]); setTotal(0); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [window]);

  return (
    <div className="bg-slate-50 min-h-[80vh]" data-testid="public-events-page">
      <div className="bg-gradient-to-br from-[#0a1f47] to-[#1a3a7a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#f5c876]">Public events</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">KR Enterprise Group calendar.</h1>
          <p className="mt-3 text-slate-200 max-w-2xl">
            Public events, trainings, and community activities from KR. Sign in to your agent portal to see the full agency calendar.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 mb-6">
          {[
            { k: 'upcoming', label: 'Upcoming' },
            { k: 'past', label: 'Past' },
          ].map((tab) => (
            <button
              key={tab.k}
              onClick={() => setWindow(tab.k)}
              data-testid={`events-tab-${tab.k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                window === tab.k
                  ? 'bg-[#0f2a5e] text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-[#0f2a5e]'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <div className="ml-auto text-sm text-slate-500">{total} event{total === 1 ? '' : 's'}</div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-slate-500" data-testid="events-loading">Loading events…</p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="events-empty">
            <CalendarIcon size={32} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600">No {window} public events at the moment.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-grid">
            {items.map((ev) => <EventCard key={ev.event_id} event={ev} />)}
          </div>
        )}

        {hasMore && !loading && (
          <p className="text-xs text-slate-500 text-center mt-6">
            Showing the first 24 events. More available — refine or contact us.
          </p>
        )}
      </div>
    </div>
  );
}

const EventCard = ({ event }) => {
  const dateStr = event.start_datetime
    ? formatEventDate(event.start_datetime, event.timezone)
    : '—';
  const timeStr = event.start_datetime && !event.all_day
    ? formatEventTime(event.start_datetime, event.timezone)
    : '';
  const loc = event.physical_location;
  const isVirtual = !!event.virtual_meeting_url && !(loc?.city);
  // Phase 26.9.1 — prefer the cover image (new); fall back to the legacy
  // public flyer image for events that only have a flyer uploaded.
  const backend = process.env.REACT_APP_BACKEND_URL;
  const coverSrc = event.cover_image_url
    ? `${backend}${event.cover_image_url}`
    : publicAPI.publicFlyerUrl(event.event_id);
  // Phase 26.9.2 — themed placeholder gradient by category.
  const theme = themeForCategory(event.category);
  const gradient = `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`;
  return (
    <Link
      to={`/events/${event.event_id}`}
      className="group block rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:border-[#0f2a5e]/40 transition"
      data-testid={`public-event-card-${event.event_id}`}
    >
      <div
        className="aspect-[16/9] relative overflow-hidden"
        style={{ background: gradient }}
        data-category={event.category || 'default'}
      >
        <img
          src={coverSrc}
          alt=""
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          className="w-full h-full object-cover"
          data-testid={`public-event-card-cover-${event.event_id}`}
        />
        <div
          className="absolute top-3 left-3 rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(255,255,255,0.95)', color: theme.from }}
        >
          {theme.label}
        </div>
      </div>
      <div className="p-5">
        <div className="text-xs font-semibold text-[#b8862d] uppercase tracking-wider">
          {dateStr}{timeStr && ` · ${timeStr}`}
        </div>
        <h3 className="mt-2 text-lg font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#0f2a5e]">{event.title}</h3>
        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
          {isVirtual ? <><Video size={12} /> Virtual event</>
            : <><MapPin size={12} /> {[loc?.city, loc?.state].filter(Boolean).join(', ') || 'Location TBD'}</>}
        </div>
      </div>
    </Link>
  );
};
